<template>
  <div class="asr-hidden-works-pre-call">
    <q-linear-progress
      v-if="isPrefillLoading"
      indeterminate
      class="tw-mb-2"
    />
    <q-banner
      v-if="hasPrevAsrFloorHint"
      dense
      rounded
      class="tw-mb-3 tw-bg-blue-1 tw-text-grey-9"
    >
      <template #avatar>
        <q-icon name="info" color="primary" />
      </template>
      Данные предзаполнены по предыдущей АСР {{ prefilledFloorLabel }} этажа.
    </q-banner>
    <q-form class="form-1c" @submit.prevent="onSubmit">
      <div class="form-1c__row">
        <label class="form-1c__label">Номер документа <span class="required">*</span></label>
        <div class="form-1c__field">
          <CustomInput
            type="text"
            :required="true"
            :value="document_num"
            :placeholder="documentNumPlaceholder ? `Предыдущий номер: ${documentNumPlaceholder}` : 'Введите номер документа'"
            @change_input="(v) => onChange('document_num', v)"
          />
        </div>
      </div>
      <div class="form-1c__row">
        <label class="form-1c__label">Дата документа <span class="required">*</span></label>
        <div class="form-1c__field">
          <CustomDate
            :required="true"
            :full-width="true"
            placeholder="ДД.ММ.ГГГГ"
            :value="document_date"
            :max-date="documentMaxDate"
            :default-value="documentMaxDate"
            @change_date="(v) => (document_date = v)"
          />
        </div>
      </div>
      <div class="form-1c__row">
        <label class="form-1c__label">Идентификационные параметры эскиза <span class="required">*</span></label>
        <div class="form-1c__field">
          <CustomInput
            :required="true"
            :value="work_comment"
            placeholder="429-4-18-КЖ Лист 25-35"
            @change_input="(v) => (work_comment = v)"
          />
        </div>
      </div>
      <div class="form-1c__row">
        <label class="form-1c__label">К освидетельствованию предъявлены работы <span class="required">*</span></label>
        <div class="form-1c__field">
          <CustomInput
            type="textarea"
            :rows="3"
            :required="true"
            :value="work_sets"
            placeholder="Наименование скрытых работ"
            @change_input="(v) => onChange('work_sets', v)"
          />
        </div>
      </div>
      <div class="form-1c__row">
        <label class="form-1c__label">При выполнении работ применены <span class="required">*</span></label>
        <div class="form-1c__field">
          <CustomInput
            type="textarea"
            :rows="3"
            :required="true"
            :value="materials"
            placeholder="Наименование материалов, конструкций, изделий и т.д."
            @change_input="(v) => onChange('materials', v)"
          />
        </div>
      </div>
      <div class="form-1c__row">
        <label class="form-1c__label">Разрешается производство последующих работ <span class="required">*</span></label>
        <div class="form-1c__field">
          <CustomInput
            type="textarea"
            :rows="3"
            :required="true"
            :value="work_asr_name"
            placeholder="Наименование последующих работ и конструкций"
            @change_input="(v) => onChange('work_asr_name', v)"
          />
        </div>
      </div>
      <div class="form-1c__row">
        <label class="form-1c__label">Дата начала работ <span class="required">*</span></label>
        <div class="form-1c__field">
          <CustomDate
            :required="true"
            :full-width="true"
            placeholder="ДД.ММ.ГГГГ"
            :value="work_date_begin"
            @change_date="(v) => (work_date_begin = v)"
          />
        </div>
      </div>
      <div class="form-1c__row">
        <label class="form-1c__label">Дата окончания работ <span class="required">*</span></label>
        <div class="form-1c__field">
          <CustomDate
            :required="true"
            :full-width="true"
            placeholder="ДД.ММ.ГГГГ"
            :value="work_date_end"
            @change_date="(v) => (work_date_end = v)"
          />
        </div>
      </div>
      <CustomModalFooter
        submit-btn-text="Создать"
        :btn-loading="isSubmitting"
      />
    </q-form>
  </div>
</template>

<script>
import CustomInput from '@/components/form/CustomInput'
import CustomDate from '@/components/form/CustomDate'
import CustomModalFooter from '@/components/modal/modals/modal-footer'
import { createAsrPreCall, getLastAsrPreCall } from '@/views/ResidentialSettings/services'
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar'
import { getLocaleDate } from '@/services'

export default {
  name: 'AsrHiddenWorksPreCall',
  components: { CustomInput, CustomDate, CustomModalFooter },
  props: {
    data: { type: Object, required: true },
    closeModal: { type: Function, required: true },
  },
  data() {
    return {
      work_comment: '',
      documentNumPlaceholder: '',
      document_num: '',
      document_date: getLocaleDate(new Date()),
      work_date_begin: '',
      work_date_end: '',
      work_sets: '', 
      materials: '', 
      work_asr_name: '',
      isSubmitting: false,
      isPrefillLoading: false,
      prevAsrPreCallLevelNum: null,
    }
  },
  mounted() {
    this.applyLastAsrPrefill()
  },
  computed: {
    documentMaxDate() {
      const d = new Date()
      d.setHours(0, 0, 0, 0)
      return d
    },
    hasPrevAsrFloorHint() {
      return (
        this.prevAsrPreCallLevelNum != null &&
        String(this.prevAsrPreCallLevelNum).trim() !== ''
      )
    },
    prefilledFloorLabel() {
      return String(this.prevAsrPreCallLevelNum).trim()
    },
  },
  methods: {
    onChange(field, value) {
      this[field] = value
    },
    async onSubmit() {
      const floorMapId = this.data?.floorMapId
      const placement_type_id = this.data?.placementTypeId
      const work_set_check_group_id = this.data?.workSetCheckGroupId
      if (!floorMapId || placement_type_id == null || work_set_check_group_id == null) {
        this.showErrorSnackbar('Не хватает данных для создания документа')
        return
      }
      if (!String(this.work_comment || '').trim()) {
        this.showErrorSnackbar('Укажите идентификационные данные эскиза')
        return
      }
      if (!this.document_date) {
        this.showErrorSnackbar('Укажите дату документа')
        return
      }
      if (!this.work_date_begin || !this.work_date_end) {
        this.showErrorSnackbar('Укажите даты начала и окончания работ')
        return
      }

      const body = {
        placement_type_id,
        work_set_check_group_id,
        work_comment: String(this.work_comment).trim(),
        work_date_begin: this.work_date_begin,
        work_date_end: this.work_date_end,
        document_date: this.document_date,
        floor_map_id: this.data?.floorMapId,
        work_sets: String(this.work_sets ?? '').trim(),
        materials: String(this.materials ?? '').trim(),
        work_asr_name: String(this.work_asr_name ?? '').trim(),
        document_num: this.document_num
      }

      this.isSubmitting = true
      const res = await createAsrPreCall(body, this.data?.documentParams || {})
      this.isSubmitting = false
      if (!res) return
      this.showSucceedSnackbar('Документ создан')
      if (typeof this.data.onSuccess === 'function') {
        this.data.onSuccess(res.data)
      }
      this.closeModal()
    },
    async applyLastAsrPrefill() {
      const res = await getLastAsrPreCall(this.data.floorMapId, {
        placement_type_id: this.data.placementTypeId,
        work_set_check_group_id: this.data.workSetCheckGroupId,
      })
      if(!res) return
      this.documentNumPlaceholder = res.document_num
      this.materials = res.materials
      this.work_asr_name = res.work_asr_name
      this.work_date_begin = res.work_date_begin
      this.work_date_end = res.work_date_end
      this.document_date = res.document_date
      this.work_comment = res.work_comment
      this.work_sets = res.work_sets
      this.prevAsrPreCallLevelNum = res.level_num || ''
    }
  },
  setup() {
    const { showErrorSnackbar, showSucceedSnackbar } = useCustomSnackbar()
    return { showErrorSnackbar, showSucceedSnackbar }
  },
}
</script>

<style scoped lang="scss">
.asr-hidden-works-pre-call {
  min-width: 750px;
  max-width: 100%;

  @media (max-width: 768px) {
    min-width: 100%;
  } 
  .form-1c {
    margin-top: 8px;

    &__row {
      margin-bottom: 12px;
    }

    &__label {
      margin-bottom: 6px;
      display: block;
      font-size: 14px;

      .required {
        color: red;
      }
    }

    &__field {
      min-width: 0;
    }
  }
}
</style>
